import { type ReactNode, useId } from 'react';
import { Emoji } from './Emoji';

/** Simple line icons for the top bar (original artwork), drawn white on the round orange buttons. */
function Svg({ children, size = 20 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}

/** The wheel of fortune: a rim with spokes, a hub and the pointer on its left. */
export const WheelIcon = () => (
  <Svg>
    <circle cx="12" cy="12" r="8.2" />
    <path d="M12 6.6V3.8M15.8 8.2l2-2M17.4 12h2.8M15.8 15.8l2 2M12 17.4v2.8M8.2 15.8l-2 2M8.2 8.2l-2-2" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
    <path d="M2 8.8 7.8 12 2 15.2Z" fill="currentColor" strokeWidth="1" />
  </Svg>
);

/** An outlined cup with two handles on a square base. */
export const TrophyIcon = () => (
  <Svg>
    <path d="M7.5 4h9v4.6a4.5 4.5 0 0 1-9 0Z" />
    <path d="M7.5 6.2H4.6v1.2a3.4 3.4 0 0 0 3.3 3.4M16.5 6.2h2.9v1.2a3.4 3.4 0 0 1-3.3 3.4M12 13.1v3.4" />
    <rect x="8.4" y="16.5" width="7.2" height="3.6" rx="0.6" />
  </Svg>
);

export const MenuIcon = () => (
  <Svg>
    <path d="M6 8h12M6 12h12M6 16h12" opacity="0.85" />
  </Svg>
);

/** An eight-tooth cog with a round hole. */
export const GearIcon = () => (
  <Svg>
    <path d="M10.2 5.3 10.7 2.8h2.6l.5 2.5 1.7.7 2.1-1.4 1.8 1.8-1.4 2.2.7 1.6 2.5.5v2.6l-2.5.5-.7 1.6 1.4 2.2-1.8 1.8-2.1-1.4-1.7.7-.5 2.5h-2.6l-.5-2.5-1.6-.7-2.2 1.4-1.8-1.8 1.4-2.2-.7-1.6-2.5-.5v-2.6l2.5-.5.7-1.6-1.4-2.2 1.8-1.8L8.5 6Z" />
    <circle cx="12" cy="12" r="3" />
  </Svg>
);

/** A door frame with an arrow leaving it. */
export const ExitIcon = () => (
  <Svg>
    <path d="M12.5 4.2H6.2v15.6h6.3M10.6 12h6.2" />
    <path d="M16.4 8.6 20.6 12l-4.2 3.4Z" fill="currentColor" />
  </Svg>
);

/** Two arrows chasing each other round a circle. */
export const RefreshIcon = () => (
  <Svg>
    <path d="M4.8 11a7.4 7.4 0 0 1 12.6-4.1M19.2 13a7.4 7.4 0 0 1-12.6 4.1" />
    <path d="M19.6 3.6v5.6H14ZM4.4 20.4v-5.6H10Z" fill="currentColor" strokeWidth="1" />
  </Svg>
);

export const CloseIcon = ({ size = 18 }: { size?: number }) => (
  <Svg size={size}>
    <path d="M6 6l12 12M18 6 6 18" strokeWidth="3" />
  </Svg>
);

export const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden>
    <path d="M5 4.5 20 12 5 19.5 7.2 12Z" fill="currentColor" />
  </svg>
);

/** The balance heart: a bright orange heart with a thick white rim. */
export function HeartIcon({ size = 30 }: { size?: number }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg viewBox="0 0 32 30" width={size} height={(size * 30) / 32} aria-hidden>
      <defs>
        <linearGradient id={`${id}-h`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ff7a1f" />
          <stop offset="1" stopColor="#f45600" />
        </linearGradient>
      </defs>
      <path
        d="M16 28.5C9.5 23.8 2 18.4 2 10.4 2 5.9 5.4 2.5 9.7 2.5c2.6 0 4.9 1.3 6.3 3.4 1.4-2.1 3.7-3.4 6.3-3.4C26.6 2.5 30 5.9 30 10.4c0 8-7.5 13.4-14 18.1Z"
        fill={`url(#${id}-h)`}
        stroke="#ffffff"
        strokeWidth="2.4"
      />
    </svg>
  );
}

/* ---- colour icons (menu, composer, music), original artwork ------------------------------------ */

/** Achievements: a gold seal with a tick. */
export const MedalIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <path
      d="m12 1 2.3 2.6 3.4-.6.9 3.3 3.2 1.4-1.1 3.3 1.1 3.3-3.2 1.4-.9 3.3-3.4-.6L12 23l-2.3-2.6-3.4.6-.9-3.3-3.2-1.4L3.3 13 2.2 9.7l3.2-1.4.9-3.3 3.4.6Z"
      fill="#f6c133"
      stroke="#e0a21a"
      strokeWidth="0.8"
    />
    <path
      d="m7.6 12.2 3 3 5.8-6.4"
      fill="none"
      stroke="#ffffff"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Ratings: a blue winners' podium. */
export const RatingsIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <rect x="1.5" y="12" width="7" height="9" fill="#5aa9e6" />
    <rect x="8.5" y="7" width="7" height="14" fill="#2f80d1" />
    <rect x="15.5" y="14" width="7" height="7" fill="#5aa9e6" />
    <path d="M1.5 12h7v1.4h-7ZM8.5 7h7v1.4h-7ZM15.5 14h7v1.4h-7Z" fill="#ffffff" opacity="0.45" />
    <path d="M1 21h22" stroke="#1f5fa8" strokeWidth="1.4" />
  </svg>
);

export const BottleMenuIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <g transform="rotate(35 12 12)">
      <rect x="10.2" y="1" width="3.6" height="4" rx="0.8" fill="#c8a24a" />
      <path
        d="M10.4 5h3.2v4.2c0 1 2.4 2 2.4 4.4V21a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-7.4c0-2.4 2.4-3.4 2.4-4.4Z"
        fill="#3fa34d"
      />
      <rect x="9.4" y="14" width="5.2" height="4" fill="#f2e6c9" />
      <rect x="9.6" y="10.5" width="1.2" height="9" rx="0.6" fill="#ffffff" opacity="0.45" />
    </g>
  </svg>
);

export const StylingIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <rect x="2" y="2" width="20" height="20" rx="2" fill="#f2b81f" />
    <rect x="5" y="5" width="14" height="14" rx="1" fill="#bfe3f7" />
    <circle cx="12" cy="10.5" r="2.8" fill="#8d6e63" />
    <path d="M6.5 19c.8-3 3-4.4 5.5-4.4s4.7 1.4 5.5 4.4Z" fill="#7e57c2" />
    <circle cx="4" cy="4" r="1.2" fill="#fff3c4" />
    <circle cx="20" cy="20" r="1.2" fill="#fff3c4" />
  </svg>
);

export const BoostersIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <path
      d="m12 1 2.3 2.6 3.4-.6.9 3.3 3.2 1.4-1.1 3.3 1.1 3.3-3.2 1.4-.9 3.3-3.4-.6L12 23l-2.3-2.6-3.4.6-.9-3.3-3.2-1.4L3.3 13 2.2 9.7l3.2-1.4.9-3.3 3.4.6Z"
      fill="#8e5bd8"
    />
    <path d="M13.3 5.5 8.6 13h3.1l-1 5.5 4.7-7.5h-3.1Z" fill="#ffd54a" />
  </svg>
);

/** The composer's sticker and gift buttons: the 3D puzzle piece and gift box. */
export const PuzzleIcon = ({ size = 24 }: { size?: number }) => <Emoji e="🧩" size={size} />;

export const GiftBoxIcon = ({ size = 24 }: { size?: number }) => <Emoji e="🎁" size={size} />;

export const MusicVideoIcon = () => (
  <svg viewBox="0 0 28 28" width="31" height="31" aria-hidden>
    <rect x="1" y="6" width="19" height="19" rx="1.5" fill="#3d4a66" />
    {[8.5, 13, 17.5, 22].map((y) => (
      <rect key={y} x="2.5" y={y - 1.2} width="2.6" height="2.4" rx="0.4" fill="#fff" />
    ))}
    <rect x="7" y="9" width="11" height="13" fill="#5cc8f2" />
    <path d="M14 3.5 26 1v15.5" stroke="#e044c8" strokeWidth="2.6" fill="none" />
    <circle cx="11.5" cy="18" r="3.4" fill="#ea50c9" />
    <circle cx="23" cy="16" r="3.4" fill="#ea50c9" />
    <path d="M14.4 18V4" stroke="#e044c8" strokeWidth="2.6" />
  </svg>
);

export const SongIcon = () => (
  <svg viewBox="0 0 28 28" width="31" height="31" aria-hidden>
    <path d="M19 22.5A10 10 0 1 1 14 4" stroke="#3d4a66" strokeWidth="5" fill="none" strokeLinecap="round" />
    <path d="M14 7 26 4.5V20" stroke="#e044c8" strokeWidth="2.6" fill="none" />
    <circle cx="11.5" cy="21" r="3.4" fill="#ea50c9" />
    <circle cx="23" cy="19.5" r="3.4" fill="#ea50c9" />
    <path d="M14.4 21V6.6" stroke="#e044c8" strokeWidth="2.6" />
  </svg>
);

export const SpeakerIcon = ({ muted }: { muted: boolean }) => (
  <svg viewBox="0 0 24 20" width="20" height="17" aria-hidden>
    <path d="M2 7h4l5-4.5v15L6 13H2Z" fill="#7d8aa8" />
    {muted ? (
      <path d="m15 6.5 6 6m0-6-6 6" stroke="#f0533a" strokeWidth="2.2" strokeLinecap="round" />
    ) : (
      <path
        d="M14.5 6.5a5 5 0 0 1 0 7M17.5 4a8.5 8.5 0 0 1 0 12"
        stroke="#7d8aa8"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
    )}
  </svg>
);

/** A small red heart used next to prices. */
export const PriceHeart = () => (
  <svg viewBox="0 0 32 30" className="price-heart" aria-hidden>
    <path
      d="M16 28.5C9.5 23.8 2 18.4 2 10.4 2 5.9 5.4 2.5 9.7 2.5c2.6 0 4.9 1.3 6.3 3.4 1.4-2.1 3.7-3.4 6.3-3.4C26.6 2.5 30 5.9 30 10.4c0 8-7.5 13.4-14 18.1Z"
      fill="#f4511e"
    />
  </svg>
);

/** The GM coin: a dark brown face in a double gold rim, cream "GM" letters. */
export function GmCoin({ size = 16 }: { size?: number }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden className="gm-coin">
      <defs>
        <linearGradient id={`${id}-g`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f6d488" />
          <stop offset="0.3" stopColor="#c99232" />
          <stop offset="0.55" stopColor="#f3cf7c" />
          <stop offset="0.8" stopColor="#b98326" />
          <stop offset="1" stopColor="#e8bd62" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="11.6" fill={`url(#${id}-g)`} />
      <circle cx="12" cy="12" r="10.6" fill="#2e2013" />
      <circle cx="12" cy="12" r="9.5" fill="none" stroke={`url(#${id}-g)`} strokeWidth="0.75" />
      <circle cx="12" cy="12" r="9.1" fill="#2e2013" />
      <text
        x="12"
        y="16.1"
        textAnchor="middle"
        fontSize="11.4"
        fontWeight="700"
        letterSpacing="-0.7"
        fill="#f4e7c9"
        fontFamily="Arial, sans-serif"
      >
        GM
      </text>
    </svg>
  );
}

/** Two ivory domino tiles side by side (6|3 and 5|6). */
export const DominoIcon = ({ size = 18 }: { size?: number }) => {
  const pips: Record<number, [number, number][]> = {
    3: [
      [2.6, 2.6],
      [5, 5],
      [7.4, 7.4],
    ],
    5: [
      [2.6, 2.6],
      [7.4, 2.6],
      [5, 5],
      [2.6, 7.4],
      [7.4, 7.4],
    ],
    6: [
      [2.8, 2.4],
      [7.2, 2.4],
      [2.8, 5],
      [7.2, 5],
      [2.8, 7.6],
      [7.2, 7.6],
    ],
  };
  const tile = (x: number, rot: number, top: number, bottom: number) => (
    <g transform={`translate(${x} 2) rotate(${rot} 5 10)`}>
      <rect width="10" height="20" rx="2" fill="#fbf6ea" stroke="#8a6a3e" strokeWidth="0.8" />
      <path d="M1.5 10h7" stroke="#8a6a3e" strokeWidth="0.7" />
      {pips[top]!.map(([px, py], i) => (
        <circle key={`t${i}`} cx={px} cy={py} r="0.95" fill="#222" />
      ))}
      {pips[bottom]!.map(([px, py], i) => (
        <circle key={`b${i}`} cx={px} cy={py + 10} r="0.95" fill="#222" />
      ))}
    </g>
  );
  return (
    <svg viewBox="0 0 26 24" width={size} height={size} aria-hidden className="domino-icon">
      {tile(1.5, -8, 6, 3)}
      {tile(14.5, 8, 5, 6)}
    </svg>
  );
};
